import { LOCALE_OPTIONS } from '@/constants/enum';
import { IHoliday, ISpecificWorkingDay } from '@/types/components/day';
import { format } from 'date-fns/format';
import {
  enUS,
  enGB,
  es,
  zhCN,
  hi,
  ar,
  fr,
  ru,
  ja,
  de,
  pt,
  it,
  ko,
  vi,
  tr,
  nl,
  th,
  sv,
  da,
  faIR,
  arDZ,
  arEG,
  arMA,
  arSA,
  arTN,
  be,
  beTarask,
  bg,
  bn,
  bs,
  ca,
  ckb,
  cs,
  cy,
  eo,
  et,
  eu,
  fi,
  frCA,
  frCH,
  fy,
  gd,
  gl,
  gu,
  he,
  hr,
  ht,
  hu,
  hy,
  id,
  is,
  itCH,
  jaHira,
  ka,
  kk,
  km,
  kn,
  lb,
  lt,
  lv,
  mk,
  mn,
  ms,
  mt,
  nb,
  nlBE,
  nn,
  oc,
  ptBR,
  ro,
  se,
  sk,
  sl,
  sr,
  srLatn,
  ta,
  te,
  ug,
  uz,
  uzCyrl,
  zhHK,
  zhTW,
  pl,
  Locale,
  af,
  az,
  el,
  enAU,
  enCA,
  enIE,
  deAT,
  enIN,
  enNZ,
  enZA,
  sq,
  uk,
} from 'date-fns/locale';

type GroupDateType = {
  [year: number]: {
    [month: number]: number[];
  };
};

export const localeMapping: Record<LOCALE_OPTIONS, Locale> = {
  [LOCALE_OPTIONS.EN_US]: enUS,
  [LOCALE_OPTIONS.EN_GB]: enGB,
  [LOCALE_OPTIONS.ES]: es,
  [LOCALE_OPTIONS.ZH_CN]: zhCN,
  [LOCALE_OPTIONS.HI]: hi,
  [LOCALE_OPTIONS.AR]: ar,
  [LOCALE_OPTIONS.FR]: fr,
  [LOCALE_OPTIONS.RU]: ru,
  [LOCALE_OPTIONS.JA]: ja,
  [LOCALE_OPTIONS.DE]: de,
  [LOCALE_OPTIONS.PT]: pt,
  [LOCALE_OPTIONS.IT]: it,
  [LOCALE_OPTIONS.KO]: ko,
  [LOCALE_OPTIONS.VI]: vi,
  [LOCALE_OPTIONS.TR]: tr,
  [LOCALE_OPTIONS.PL]: pl,
  [LOCALE_OPTIONS.NL]: nl,
  [LOCALE_OPTIONS.TH]: th,
  [LOCALE_OPTIONS.SV]: sv,
  [LOCALE_OPTIONS.DA]: da,
  [LOCALE_OPTIONS.FA_IR]: faIR,
  [LOCALE_OPTIONS.AR_DZ]: arDZ,
  [LOCALE_OPTIONS.AR_EG]: arEG,
  [LOCALE_OPTIONS.AR_MA]: arMA,
  [LOCALE_OPTIONS.AR_SA]: arSA,
  [LOCALE_OPTIONS.AR_TN]: arTN,
  [LOCALE_OPTIONS.BE]: be,
  [LOCALE_OPTIONS.BE_TARASK]: beTarask,
  [LOCALE_OPTIONS.BG]: bg,
  [LOCALE_OPTIONS.BN]: bn,
  [LOCALE_OPTIONS.BS]: bs,
  [LOCALE_OPTIONS.CA]: ca,
  [LOCALE_OPTIONS.CKB]: ckb,
  [LOCALE_OPTIONS.CS]: cs,
  [LOCALE_OPTIONS.CY]: cy,
  [LOCALE_OPTIONS.EO]: eo,
  [LOCALE_OPTIONS.ET]: et,
  [LOCALE_OPTIONS.EU]: eu,
  [LOCALE_OPTIONS.FI]: fi,
  [LOCALE_OPTIONS.FR_CA]: frCA,
  [LOCALE_OPTIONS.FR_CH]: frCH,
  [LOCALE_OPTIONS.FY]: fy,
  [LOCALE_OPTIONS.GD]: gd,
  [LOCALE_OPTIONS.GL]: gl,
  [LOCALE_OPTIONS.GU]: gu,
  [LOCALE_OPTIONS.HE]: he,
  [LOCALE_OPTIONS.HR]: hr,
  [LOCALE_OPTIONS.HT]: ht,
  [LOCALE_OPTIONS.HU]: hu,
  [LOCALE_OPTIONS.HY]: hy,
  [LOCALE_OPTIONS.ID]: id,
  [LOCALE_OPTIONS.IS]: is,
  [LOCALE_OPTIONS.IT_CH]: itCH,
  [LOCALE_OPTIONS.JA_HIRA]: jaHira,
  [LOCALE_OPTIONS.KA]: ka,
  [LOCALE_OPTIONS.KK]: kk,
  [LOCALE_OPTIONS.KM]: km,
  [LOCALE_OPTIONS.KN]: kn,
  [LOCALE_OPTIONS.LB]: lb,
  [LOCALE_OPTIONS.LT]: lt,
  [LOCALE_OPTIONS.LV]: lv,
  [LOCALE_OPTIONS.MK]: mk,
  [LOCALE_OPTIONS.MN]: mn,
  [LOCALE_OPTIONS.MS]: ms,
  [LOCALE_OPTIONS.MT]: mt,
  [LOCALE_OPTIONS.NB]: nb,
  [LOCALE_OPTIONS.NL_BE]: nlBE,
  [LOCALE_OPTIONS.NN]: nn,
  [LOCALE_OPTIONS.OC]: oc,
  [LOCALE_OPTIONS.PT_BR]: ptBR,
  [LOCALE_OPTIONS.RO]: ro,
  [LOCALE_OPTIONS.SE]: se,
  [LOCALE_OPTIONS.SK]: sk,
  [LOCALE_OPTIONS.SL]: sl,
  [LOCALE_OPTIONS.SR]: sr,
  [LOCALE_OPTIONS.SR_LATN]: srLatn,
  [LOCALE_OPTIONS.TA]: ta,
  [LOCALE_OPTIONS.TE]: te,
  [LOCALE_OPTIONS.UG]: ug,
  [LOCALE_OPTIONS.UZ]: uz,
  [LOCALE_OPTIONS.UZ_CYRL]: uzCyrl,
  [LOCALE_OPTIONS.ZH_HK]: zhHK,
  [LOCALE_OPTIONS.ZH_TW]: zhTW,
  [LOCALE_OPTIONS.AF]: af,
  [LOCALE_OPTIONS.AZ]: az,
  [LOCALE_OPTIONS.DE_AT]: deAT,
  [LOCALE_OPTIONS.EL]: el,
  [LOCALE_OPTIONS.EN_AU]: enAU,
  [LOCALE_OPTIONS.EN_CA]: enCA,
  [LOCALE_OPTIONS.EN_IE]: enIE,
  [LOCALE_OPTIONS.EN_IN]: enIN,
  [LOCALE_OPTIONS.EN_NZ]: enNZ,
  [LOCALE_OPTIONS.EN_ZA]: enZA,
  [LOCALE_OPTIONS.SQ]: sq,
  [LOCALE_OPTIONS.UK]: uk,
};

export const formatDateTime = (date: Date, dateFormat = 'EEE, dd MMM yyyy', locale = enUS) => {
  return format(date, dateFormat, { locale });
};

export function getWeekdaysFromSpecificWorkingDays(workingDays: ISpecificWorkingDay[]): number[] {
  const foundDays = new Set<number>();

  for (const day of workingDays) {
    const dates = day.dates;

    for (const date of dates) {
      const weekday = new Date(day.year, day.month - 1, Number(date)).getDay();
      foundDays.add(weekday);

      // Stop early if all days of the week (0-6) are found
      if (foundDays.size === 7) {
        return Array.from(foundDays).sort();
      }
    }
  }

  return Array.from(foundDays).sort();
}

export function groupDatesByYearMonthDay({ start, end }: { start: Date; end: Date }) {
  const result: GroupDateType = {};
  let currentDate = new Date(start);

  while (currentDate <= end) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1; // Tháng bắt đầu từ 0
    const day = currentDate.getDate();

    if (!result[year]) {
      result[year] = {};
    }
    if (!result[year][month]) {
      result[year][month] = [];
    }

    result[year][month].push(day);

    // Chuyển sang ngày tiếp theo
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return result;
}

export function formatDateToYearMonthDayDateString(date: Date) {
  const year = String(date.getFullYear());
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  if (month.length < 2) {
    month = String(month).padStart(2, '0');
  }
  if (day.length < 2) {
    day = String(day).padStart(2, '0');
  }
  return [year, month, day].join('-');
}

export function specificDateProcessMerge(groupedDates: GroupDateType) {
  // Chuyển đổi groupedDates thành danh sách các phần tử ngày/tháng/năm
  const newSpecificDays = Object.entries(groupedDates).flatMap(([year, months]) => {
    return Object.entries(months).map(([month, dates]) => {
      return {
        year: Number(year),
        month: Number(month),
        dates,
      };
    });
  });

  return [...newSpecificDays].sort((a, b) => a.year - b.year || a.month - b.month);
}

export function specificDateProcess(
  groupedDates: GroupDateType,
  specificDays: ISpecificWorkingDay[] | IHoliday[],
) {
  const newSpecificDays = Object.entries(groupedDates).flatMap(([year, months]) => {
    return Object.entries(months).map(([month, dates]) => {
      return {
        year: Number(year),
        month: Number(month),
        dates,
      };
    });
  });

  // merge newSpecificDays with existing pickupSpecificDays, if entry already exists, then merge the dates
  const mergedSpecificDays = newSpecificDays.map((newSpecificDay) => {
    const existingSpecificDay = specificDays.find(
      (specificDay) => specificDay.year === newSpecificDay.year && specificDay.month === newSpecificDay.month,
    );

    if (existingSpecificDay) {
      const currentMonthDates = (existingSpecificDay.dates || []) as unknown as number[];
      const newCurrentMonthDates = newSpecificDay.dates || [];
      const mergedDates = Array.from(new Set([...currentMonthDates, ...newCurrentMonthDates]));
      return {
        ...existingSpecificDay,
        dates: mergedDates,
      };
    }

    return newSpecificDay;
  });

  // remove existing specific rows that are being merged, based on year and month
  const filteredSpecificDays = specificDays.filter(
    (specificDay) =>
      !mergedSpecificDays.some(
        (mergedSpecificDay) => specificDay.year === mergedSpecificDay.year && specificDay.month === mergedSpecificDay.month,
      ),
  );

  return [...filteredSpecificDays, ...mergedSpecificDays];
}

export function removeSpecificDateProcess(
  groupedDates: GroupDateType,
  specificDays: ISpecificWorkingDay[],
) {
  // Hàm để loại bỏ ngày đã có trong groupedDates
  const removeDatesFromGroup = (dates: number[], year: number, month: number): number[] => {
    const groupedYear = groupedDates[year];
    if (!groupedYear) return dates;

    const groupedMonthDates = groupedYear[month];
    if (!groupedMonthDates) return dates;

    // Loại bỏ những ngày có trong groupedMonthDates
    return dates.filter((date) => !groupedMonthDates.includes(date));
  };

  // Hợp nhất pickupSpecificDays nhưng loại bỏ các ngày có trong groupedDates
  const filteredSpecificDays = specificDays.map((specificDay) => {
    const filteredDates = removeDatesFromGroup(specificDay.dates as number[], specificDay.year, specificDay.month);

    // Trả về entry mới với danh sách ngày đã được lọc
    return {
      ...specificDay,
      dates: filteredDates,
    };
  });

  // Loại bỏ các mục mà không còn ngày nào sau khi lọc
  const finalSpecificDays = filteredSpecificDays.filter((specificDay) => specificDay.dates.length > 0);

  return finalSpecificDays;
}

export const time24hTo12h = (time: string) => {
  const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  if (!regex.test(time)) {
    return time;
  }
  const [hour, minute] = time.split(':');
  const hour12 = parseInt(hour) % 12 || 12;
  const ampm = parseInt(hour) >= 12 ? 'PM' : 'AM';

  return `${hour12}:${minute} ${ampm}`;
};

export const convertTime24h = (time: string) => {
  const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  if (!regex.test(time)) {
    return time;
  }
  const [hour, minute] = time.split(':');
  const hour24 = parseInt(hour);

  return `${hour24}:${minute}`;
};
